/**
 * Build planes — flat surfaces you add yourself.
 *
 * An imported model does not always carry the surface a scaffold must stand
 * on: a slab the file omits, a roof that was never modelled, the floor of an
 * upper storey. A build plane is a horizontal rectangle placed anywhere in
 * world space that joins the collider set, so standards stand on it exactly
 * as they stand on a roof of the reference model. Planes are construction
 * aids, not components: they are saved with the design but never exported or
 * booked, and they sit outside the scaffold's undo history.
 *
 * Each plane is a Group — position, yaw, and scale = its size — carrying a
 * unit slab and its outline. The transform gizmo attaches to the Group for
 * moving and rotating, so translate and rotate-about-Y map straight onto the
 * fields; resizing uses corner handles instead (`PlaneCorners`), which keep
 * the opposite corner still rather than scaling about the centre.
 */

import {
  Box3,
  type Camera,
  DoubleSide,
  EdgesGeometry,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshStandardMaterial,
  type Object3D,
  PlaneGeometry,
  type Scene,
} from "three";
import type { TransformControlsMode } from "three/addons/controls/TransformControls.js";
import type { CameraRig } from "./cameraRig.ts";
import { Gizmo } from "./gizmo.ts";
import { HAKI_BLUE } from "./materials.ts";
import type { Picker } from "./picking.ts";
import { PlaneCorners } from "./planeCorners.ts";

export type BuildPlane = Readonly<{
  id: string;
  name: string;
  /** Centre, world metres. */
  x: number;
  y: number;
  z: number;
  /** Size along the plane's own X and Z, metres. */
  w: number;
  d: number;
  /** Yaw, degrees. */
  rot: number;
  /** 0–100. */
  opacity: number;
  visible: boolean;
  /** Standards may stand on it. */
  collide: boolean;
}>;

export type PlaneGizmoMode = "move" | "resize" | "rotate";

/** The fields the gizmo can change. */
export type PlanePatch = Readonly<Partial<Pick<BuildPlane, "x" | "y" | "z" | "w" | "d" | "rot">>>;

export type PlaneEvents = Readonly<{
  /** `done` is true once the drag has ended — the moment to clamp and re-solve. */
  onEdit: (id: string, patch: PlanePatch, done: boolean) => void;
}>;

const DEG = Math.PI / 180;
const PLANE_COLOR = 0x4a9aa8;
const OUTLINE_COLOR = 0x2f6f7a;
const SELECTED_OUTLINE_COLOR = 0xffffff;
const SELECTED_GLOW = 0.25;

/** Gizmo snapping: metres and radians. */
const TRANSLATION_SNAP = 0.1;
const ROTATION_SNAP = Math.PI / 12;

/** Below this opacity a plane stops casting a shadow — it reads as a guide, not a slab. */
const SHADOW_OPACITY = 0.5;

/** Resize has no gizmo mode — it is the corner handles. */
const GIZMO_MODES: Readonly<Record<Exclude<PlaneGizmoMode, "resize">, TransformControlsMode>> = {
  move: "translate",
  rotate: "rotate",
};

const SLAB_GEOMETRY = new PlaneGeometry(1, 1);
const OUTLINE_GEOMETRY = new EdgesGeometry(SLAB_GEOMETRY);

type Entry = Readonly<{
  group: Group;
  slab: Mesh;
  material: MeshStandardMaterial;
  line: LineBasicMaterial;
}>;

function buildEntry(id: string): Entry {
  const material = new MeshStandardMaterial({
    color: PLANE_COLOR,
    side: DoubleSide,
    transparent: true,
    opacity: 0.6,
    depthWrite: false,
    roughness: 0.9,
    metalness: 0,
  });
  const slab = new Mesh(SLAB_GEOMETRY, material);
  slab.rotation.x = -Math.PI / 2;
  slab.receiveShadow = true;
  slab.userData = { planeId: id };

  const line = new LineBasicMaterial({ color: OUTLINE_COLOR });
  const outline = new LineSegments(OUTLINE_GEOMETRY, line);
  outline.rotation.x = -Math.PI / 2;
  outline.position.y = 0.002;

  const group = new Group();
  group.add(slab, outline);
  group.userData = { planeId: id };

  return { group, slab, material, line };
}

/** Yaw of a rotation about Y in degrees within (-180, 180]. Read from the quaternion — Euler angles flip past ±90°. */
function yawDegrees(group: Group): number {
  const { y, w } = group.quaternion;
  let degrees = (2 * Math.atan2(y, w)) / DEG;

  while (degrees <= -180) {
    degrees += 360;
  }
  while (degrees > 180) {
    degrees -= 360;
  }

  return degrees;
}

export class BuildPlanes {
  readonly group = new Group();

  private readonly entries = new Map<string, Entry>();
  private planes: readonly BuildPlane[] = [];
  private selected: string | null = null;
  private gizmoMode: PlaneGizmoMode = "move";
  private snap = true;
  private gizmoEnabled = false;
  private gizmo: Gizmo | null = null;
  private corners: PlaneCorners | null = null;
  private scene: Scene | null = null;
  private readonly events: PlaneEvents;

  constructor(events: PlaneEvents) {
    this.events = events;
  }

  get selectedId(): string | null {
    return this.selected;
  }

  get mode(): PlaneGizmoMode {
    return this.gizmoMode;
  }

  /** Slabs a standard may stand on. */
  get colliderMeshes(): Object3D[] {
    return this.slabsWhere((plane) => plane.visible && plane.collide);
  }

  /** Slabs the pointer can pick. */
  get pickMeshes(): Object3D[] {
    return this.slabsWhere((plane) => plane.visible);
  }

  private slabsWhere(test: (plane: BuildPlane) => boolean): Object3D[] {
    const slabs: Object3D[] = [];
    for (const plane of this.planes) {
      const entry = this.entries.get(plane.id);
      if (entry && test(plane)) {
        slabs.push(entry.slab);
      }
    }

    return slabs;
  }

  planeById(id: string): BuildPlane | undefined {
    return this.planes.find((plane) => plane.id === id);
  }

  /** The plane a picked object belongs to — slabs and outlines carry the id up the parent chain. */
  planeIdOf(object: Object3D): string | null {
    let node: Object3D | null = object;
    while (node) {
      const id: unknown = node.userData.planeId;
      if (typeof id === "string") {
        return id;
      }
      node = node.parent;
    }

    return null;
  }

  /* ---------------------------------------------------------------------------
     Gizmo lifecycle
     --------------------------------------------------------------------------- */

  attach(scene: Scene, camera: Camera, domElement: HTMLElement, rig: CameraRig, picker: Picker): void {
    this.scene = scene;
    this.gizmo = new Gizmo(scene, camera, domElement, rig, {
      change: () => this.emitSelected(false),
      dragEnd: () => this.emitSelected(true),
    });
    this.corners = new PlaneCorners(picker, rig, domElement, {
      change: () => this.emitSelected(false),
      dragEnd: () => this.emitSelected(true),
    });
    scene.add(this.corners.group);
    this.attachSelected();
  }

  detach(): void {
    this.gizmo?.dispose();
    this.gizmo = null;
    if (this.corners) {
      this.scene?.remove(this.corners.group);
      this.corners.dispose();
      this.corners = null;
    }
    this.scene = null;
  }

  /** Keep the corner handles a constant size on screen. Called once per frame. */
  update(camera: Camera): void {
    this.corners?.update(camera);
  }

  /** Did the press that ends in this click begin on a gizmo or corner handle? */
  wasPressOnGizmo(): boolean {
    return (this.gizmo?.wasPressOn() ?? false) || (this.corners?.wasPressOn() ?? false);
  }

  /** Is the pointer over a gizmo or corner handle right now? */
  isPointerOnGizmo(): boolean {
    return (this.gizmo?.isPointerOn() ?? false) || (this.corners?.isPointerOn() ?? false);
  }

  /* ---------------------------------------------------------------------------
     State → scene
     --------------------------------------------------------------------------- */

  /** Mirror the store: create, update and remove slabs, then re-attach the gizmo. */
  sync(planes: readonly BuildPlane[], selectedId: string | null): void {
    this.planes = planes;
    const seen = new Set<string>();

    for (const plane of planes) {
      seen.add(plane.id);
      let entry = this.entries.get(plane.id);
      if (!entry) {
        entry = buildEntry(plane.id);
        this.entries.set(plane.id, entry);
        this.group.add(entry.group);
      }
      this.apply(entry, plane, plane.id === selectedId);
    }

    for (const [id, entry] of this.entries) {
      if (!seen.has(id)) {
        this.group.remove(entry.group);
        entry.material.dispose();
        entry.line.dispose();
        this.entries.delete(id);
      }
    }

    this.selected = selectedId !== null && seen.has(selectedId) ? selectedId : null;
    this.attachSelected();
    this.corners?.place();
  }

  private apply(entry: Entry, plane: BuildPlane, selected: boolean): void {
    entry.group.position.set(plane.x, plane.y, plane.z);
    entry.group.rotation.set(0, plane.rot * DEG, 0);
    entry.group.scale.set(plane.w, 1, plane.d);
    entry.group.visible = plane.visible;

    const opacity = plane.opacity / 100;
    const solid = opacity >= 0.995;
    entry.material.opacity = solid ? 1 : opacity;
    entry.material.transparent = !solid;
    entry.material.depthWrite = solid;
    entry.material.emissive.setHex(selected ? HAKI_BLUE : 0);
    entry.material.emissiveIntensity = selected ? SELECTED_GLOW : 0;
    entry.material.needsUpdate = true;
    entry.slab.castShadow = opacity >= SHADOW_OPACITY;
    entry.line.color.setHex(selected ? SELECTED_OUTLINE_COLOR : OUTLINE_COLOR);
  }

  setMode(mode: PlaneGizmoMode): void {
    this.gizmoMode = mode;
    this.applyGizmoSettings();
  }

  setSnap(snap: boolean): void {
    this.snap = snap;
    this.applyGizmoSettings();
  }

  /** The gizmo only answers the pointer while the Plane tool is active. */
  setGizmoEnabled(enabled: boolean): void {
    this.gizmoEnabled = enabled;
    this.applyGizmoSettings();
  }

  /**
   * A plane stays horizontal: move on all axes (Y is its height), rotate only
   * about Y. Resize hands over to the corner handles and hides the gizmo.
   */
  private applyGizmoSettings(): void {
    const { gizmo, corners, gizmoMode } = this;
    const resizing = gizmoMode === "resize";

    if (gizmo) {
      const { controls } = gizmo;
      controls.setMode(GIZMO_MODES[gizmoMode === "resize" ? "move" : gizmoMode]);
      controls.setTranslationSnap(this.snap ? TRANSLATION_SNAP : null);
      controls.setRotationSnap(this.snap ? ROTATION_SNAP : null);
      controls.showX = this.gizmoMode !== "rotate";
      controls.showZ = this.gizmoMode !== "rotate";
      controls.showY = true;
      controls.space = this.gizmoMode === "move" ? "world" : "local";
      gizmo.setActive(this.gizmoEnabled && !resizing);
    }
    if (corners) {
      corners.setSnap(this.snap);
      corners.setActive(this.gizmoEnabled && resizing);
    }
  }

  private attachSelected(): void {
    const entry = this.selected !== null ? this.entries.get(this.selected) : undefined;
    this.gizmo?.attach(entry ? entry.group : null);
    this.corners?.attach(entry ? entry.group : null);
    this.applyGizmoSettings();
  }

  private emitSelected(done: boolean): void {
    if (this.selected !== null) {
      this.emit(this.selected, done);
    }
  }

  private emit(id: string, done: boolean): void {
    const entry = this.entries.get(id);
    if (!entry) {
      return;
    }

    const { position, scale } = entry.group;
    this.events.onEdit(
      id,
      { x: position.x, y: position.y, z: position.z, w: scale.x, d: scale.z, rot: yawDegrees(entry.group) },
      done,
    );
  }

  boundsOf(id: string): Box3 | null {
    const entry = this.entries.get(id);

    return entry ? new Box3().setFromObject(entry.group) : null;
  }
}
